import React, { useCallback, useContext, useState } from 'react'
import { BoxProps, Link } from 'rebass'

import HomeSvg from '../../../public/HomeSvg.svg'
import GridSvg from '../../../public/ResourcesSvg.svg'
import SettingsSvg from '../../../public/profile.svg'
import HelpSvg from '@svgs/help.svg'
import ChangePaymentMethodSvg from '@svgs/change_payment_method.svg'
import LogoutSvg from '@svgs/logout.svg'
import WebsiteSvg from '@svgs/social-web.svg'
import FacebookSvg from '@svgs/social-facebook.svg'
import InstagramSvg from '@svgs/social-instagram.svg'
import TwitterSvg from '@svgs/social-twitter.svg'

import { useSTheme } from 'styles/theme'
import ConditionalRender from 'utils/conditionalRender'

import XText from 'v2/components/XText'

import { BUTTON_STYLE_TYPE, XTEXT_DECORATIONS, XTEXT_MARGINS, XTEXT_SIZE } from 'v2/styles/constants'
import { useTranslation } from 'i18n'

import SConfirmModal from 'components/opinionless/SConfirmModal'
import XButton from 'v2/components/XButton'

import styles from './style.module.scss'
import XSideBarUserInfo from '../xSideBarUserInfo'
import XIcon from 'v2/components/XIcon'
import { Icon } from '@material-ui/core'

import { OrganizationContext } from 'v2/context/organization'
import XColors from 'v2/styles/colors'
import { UserContext } from 'v2/context/user'
import { useRouter } from 'next/router'
import { useAuth } from 'v2/hooks/useAuth'

import { ga4_linkClicked } from 'utils/ga4'

interface Props extends BoxProps {
    empty?: null
}

const XSideBar: React.FC<Props> = (props: Props) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()

    const { logoutUser } = useAuth()
    const { authenticatedUser } = useContext(UserContext)
    const organizationContext = useContext(OrganizationContext)

    const [resetCacheConfirm, setResetCacheConfirm] = useState(false)

    const onClickSignOut = useCallback((e: any) => {
        setTimeout(logoutUser, 0)
    }, [])

    const isAdmin = window.location.href.includes('admin')
    const router = useRouter()

    if (!authenticatedUser) {
        return null
    }

    return (
        <div className={styles.wrapper}>
            {!isAdmin || process.env.NEXT_PUBLIC_ADMIN_ENABLED !== 'yes' ? (
                <>
                    <div className={styles.top}>
                        <div className={styles.logo__wrapper}>
                            <div
                                className={styles.logo}
                                onClick={() => {
                                    router.push(
                                        `${
                                            window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                            window.PROGRAM.urlFragment
                                        }`
                                    )
                                }}
                            />
                        </div>
                        <div className={styles.top__user_info}>
                            <XSideBarUserInfo isAdminPanel={false} />
                        </div>
                        <SideBarButton
                            title={t('side_bar_button_home')}
                            icon={HomeSvg}
                            ga4_key={t('side_bar_button_home', { lang: 'en' })}
                            iconSize={18}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/`}
                        />
                        <SideBarButton
                            title={t('side_bar_button_account')}
                            icon={SettingsSvg}
                            ga4_key={t('side_bar_button_account', { lang: 'en' })}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/account`}
                            id="sign-out-button"
                        />
                        <SideBarButton
                            title={t('change_payment_method')}
                            icon={ChangePaymentMethodSvg}
                            ga4_key={t('change_payment_method', { lang: 'en' })}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/edit-payment-details`}
                        />
                        <SideBarButton
                            title={t('side_bar_button_resources')}
                            icon={GridSvg}
                            iconSize={22}
                            ga4_key={t('side_bar_button_resources', { lang: 'en' })}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/resources`}
                        />
                    </div>
                    <div className={styles.bottom}>
                        <div className={styles.bottom_wrapper}>
                            <SideBarButton
                                title={t('side_bar_button_help')}
                                ga4_key={t('side_bar_button_help', { lang: 'en' })}
                                icon={HelpSvg}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/help`}
                            />

                            <SideBarButton
                                title={t('side_bar_button_sign_out')}
                                icon={LogoutSvg}
                                href="#"
                                onClick={onClickSignOut}
                                id="sign-out-button"
                            />
                        </div>

                        <div className={styles.footer__icon_container}>
                            {organizationContext.program?.urlFragment && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs?.websiteLink}
                                    height={20}
                                >
                                    <XIcon icon={WebsiteSvg} size={20} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.facebookLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.facebookLink}
                                    height={20}
                                >
                                    <XIcon icon={FacebookSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.instagramLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.instagramLink}
                                    height={20}
                                >
                                    <XIcon icon={InstagramSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.twitterLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.twitterLink}
                                    height={20}
                                >
                                    <XIcon icon={TwitterSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                        </div>

                        <div className={styles.footer__text_container}>
                            <XText size={XTEXT_SIZE.SMALL} color={XColors.primaryColor}>
                                {t('side_bar_bottom_copyright')}
                            </XText>
                            <XText color={XColors.primaryColor} size={XTEXT_SIZE.SMALL}>
                                {t('side_bar_bottom_f4gi_description')}
                            </XText>
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <div className={styles.top}>
                        <div className={styles.logo__wrapper}>
                            <div className={styles.logo} />
                        </div>
                        <div className={styles.top__user_info}>
                            <XSideBarUserInfo isAdminPanel={true} />
                        </div>

                        <SideBarButton
                            googleIcon={'homeq'}
                            title={'Home'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/`}
                        />
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'paid'}
                                title={'Payments'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/payments/`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'sms'}
                                title={'Text Messages'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/text-messages`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'supervisor_account'}
                                title={'Users'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/users`}
                                id="sign-out-button"
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'analytics'}
                                title={'Stats'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/stats`}
                            />
                        </ConditionalRender>
                        <ConditionalRender if={authenticatedUser?.isSuperuser === 2}>
                            <SideBarButton
                                googleIcon={'settings'}
                                title={'Configurations'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/configurations`}
                            />
                        </ConditionalRender>

                        <SideBarButton
                            googleIcon={'assignment'}
                            title={'Applications'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/applications`}
                        />
                        <SideBarButton
                            googleIcon={'map'}
                            title={'Heat Map Signup'}
                            href={`${
                                window.PERSON?.applicationDetails[0].program?.urlFragment || window.PROGRAM.urlFragment
                            }/admin/heat-map-signup/`}
                        />

                        <ConditionalRender if={window.ORGANIZATION_CONFIGS.isTypeformBound}>
                            <SideBarButton
                                googleIcon={'inventory'}
                                title={'Survey Summary'}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/admin/survey`}
                            />
                        </ConditionalRender>
                    </div>
                    <div className={styles.bottom}>
                        <div className={styles.bottom_wrapper}>
                            <SideBarButton
                                title={t('side_bar_button_help')}
                                icon={HelpSvg}
                                href={`${
                                    window.PERSON?.applicationDetails[0].program?.urlFragment ||
                                    window.PROGRAM.urlFragment
                                }/help`}
                            />

                            <SideBarButton
                                title={t('side_bar_button_sign_out')}
                                icon={LogoutSvg}
                                href="#"
                                onClick={onClickSignOut}
                                id="sign-out-button"
                            />
                        </div>

                        <div className={styles.footer__icon_container}>
                            {organizationContext.program?.urlFragment && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs?.websiteLink}
                                    height={20}
                                >
                                    <XIcon icon={WebsiteSvg} size={20} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.facebookLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.facebookLink}
                                    height={20}
                                >
                                    <XIcon icon={FacebookSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.twitterLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.twitterLink}
                                    height={20}
                                >
                                    <XIcon icon={TwitterSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                            {organizationContext.organizationConfigs?.instagramLink && (
                                <Link
                                    target="__blank"
                                    href={organizationContext.organizationConfigs.instagramLink}
                                    height={20}
                                >
                                    <XIcon icon={InstagramSvg} size={14} tintColor={XColors.primaryColor} />
                                </Link>
                            )}
                        </div>

                        <div className={styles.footer__text_container}>
                            <XText
                                color={XColors.primaryColor}
                                size={XTEXT_SIZE.SMALL}
                                decorator={XTEXT_DECORATIONS.BOLD}
                            >
                                {t('side_bar_bottom_copyright')}
                            </XText>
                            <XText color={XColors.primaryColor} size={XTEXT_SIZE.SMALL}>
                                {t('side_bar_bottom_f4gi_description')}
                            </XText>
                        </div>
                    </div>
                </>
            )}
        </div>
    )
}

interface SideBarButtonProps {
    title: string
    icon?: any
    googleIcon?: string
    iconSize?: any
    href: string
    id?: string
    ga4_key?: string

    onClick?: (e: any) => void
}

const SideBarButton: React.FC<SideBarButtonProps> = (props: SideBarButtonProps) => {
    const { icon, title, href, googleIcon, iconSize, onClick, ...otherProps } = props
    const router = useRouter()
    const currentPath = router.pathname

    return (
        <Link
            target={'_self'}
            {...otherProps}
            onClick={(e) => {
                ga4_linkClicked(props.ga4_key)

                if (onClick) onClick(e)
                router.push(href)
            }}
        >
            <div
                className={
                    router.asPath === href ||
                    `${router.asPath}/` === href ||
                    (router.asPath.includes('/help') && href.includes('/help'))
                        ? `${styles.item} ${styles.item__active}`
                        : styles.item
                }
            >
                <div className={styles.item__icon_wrapper}>
                    <ConditionalRender if={icon}>
                        <XIcon icon={icon} size={iconSize ? iconSize : 20} tintColor={XColors.white2} />
                    </ConditionalRender>

                    <ConditionalRender if={googleIcon}>
                        <Icon
                            style={{
                                color: XColors.white2,
                            }}
                        >
                            {googleIcon}
                        </Icon>
                    </ConditionalRender>
                </div>

                <div className={styles.item__text_wrapper}>
                    <XText
                        size={XTEXT_SIZE.MEDIUM_BIGGER}
                        color={XColors.white2}
                        decorator={XTEXT_DECORATIONS.BOLD}
                        margins={XTEXT_MARGINS.NO_MARGINS}
                    >
                        {title}
                    </XText>
                </div>
            </div>
        </Link>
    )
}

export default XSideBar
